import React from "react";
import {DataGrid, GridColDef, GridActionsCellItem, GridToolbarContainer, GridRenderCellParams} from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import {Button} from "@mui/material";
import {Box} from "@mui/joy";
import { Experimental_CssVarsProvider as CssVarsProvider } from "@mui/material/styles";
import IServiceOrder from "../../../types/service-order.type";
import AddIcon from "@mui/icons-material/Add";
import IServiceOrderAttachment from "../../../types/service-order-attachment.type";
import moment from "moment";
import AttachmentModal from "./AttachmentModal";
import IFile from "../../../types/file.type";
import {getById} from "../../../services/file.service";
import {uploadAttachment} from "../../../services/service-order.service";
import showNotification from "../../utils/Notify";
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import {downloadFile} from "../../../services/file-download.service";

interface Props {
  serviceOrder: IServiceOrder;
  setServiceOrder: (updatedServiceOrder: IServiceOrder | ((prev: IServiceOrder) => IServiceOrder)) => void;
  readOnly?: boolean;
}

const AttachmentsList: React.FC<Props> = ({serviceOrder, setServiceOrder, readOnly = false}) => {
  const [attachmentModalOpen, setAttachmentModalOpen] = React.useState(false);

  const handleAttachmentModalClose = () => {
    setAttachmentModalOpen(false);
  }

  const handleAddClick = () => {
    setAttachmentModalOpen(true);
  };

  // Convierte formatos del backend a Moment (array con 6 o 7 elementos, string, Date, etc.)
  const toMomentFromBackend = (value: any): moment.Moment | null => {
    if (!value) return null;
    if (moment.isMoment(value)) return value as moment.Moment;

    if (Array.isArray(value)) {
      const [y, m, d, h = 0, min = 0, s = 0, ns] = value as number[];
      const ms = typeof ns === "number" ? Math.round(ns / 1_000_000) : 0;
      // Ojo: el mes del backend es 1-12 y Moment espera 0-11
      return moment([y, (m ?? 1) - 1, d, h, min, s, ms]);
    }

    if (typeof value === "string") return moment(value);
    if (value instanceof Date) return moment(value);
    return moment(value);
  };

  const normalizeAttachment = (a: any): IServiceOrderAttachment => ({
    ...a,
    attachedAt: toMomentFromBackend(a?.attachedAt),
  });



  const onSubmit = (attachment: IServiceOrderAttachment) => {

    uploadAttachment(serviceOrder.id, attachment).then(
      (response) => {
        setServiceOrder(prev => ({
          ...prev,
          attachments: [...prev.attachments, normalizeAttachment(response.data)],
        }));
        showNotification({type: 'success', message: "Documento adjuntado correctamente"});
        handleAttachmentModalClose()
      },
      (error) => {
        showNotification({type: 'error', message: error.response.data.message});
      }
    )

    handleAttachmentModalClose();
  };

  const columns: GridColDef<IServiceOrderAttachment>[] = [
    {
      field: 'id',
      headerName: 'ID',
      headerAlign: 'center',
      align: 'center',
      maxWidth: 60,
      flex: 1
    },
    {
      field: 'description',
      headerName: 'Descripcion',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1
    },
    {
      field: 'attachedBy',
      headerName: 'Subido por',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <span>{`${params.row.attachedBy.lastName + ', ' + params.row.attachedBy.name}`}</span>;
      }
    },
    {
      field: 'attachedAt',
      headerName: 'Fecha',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const m = toMomentFromBackend(params.row.attachedAt);
        return <span>{m ? m.format('DD/MM/YYYY') : ''}</span>;
      },

    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      headerAlign: 'center',
      align: 'center',
      minWidth: 20,
      flex: 1,
      cellClassName: 'actions',
      getActions: (params) => {
        return [
          <GridActionsCellItem
            icon={<CloudDownloadIcon/>}
            label="Descargar"
            className="textPrimary"
            onClick={() => downloadFile(params.row.attachment)}
            color="inherit"
          />
        ];
      },
    },
  ];

  function EditToolbar() {
    return (
      <GridToolbarContainer sx={{paddingBottom: 1}}>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="outlined"
          endIcon={<AddIcon />}
          onClick={handleAddClick}
          disabled={readOnly}
        >
          Agregar Documentacion
        </Button>
      </GridToolbarContainer>
    );
  }

  return (
    <Box sx={{ width: '100%', height: 384, typography: 'body1' }} mt={1}>

      <AttachmentModal
        isOpen={attachmentModalOpen}
        onSubmit={onSubmit}
        onClose={handleAttachmentModalClose}
      />

      <CssVarsProvider>
        <DataGrid
          slots={{toolbar: EditToolbar}}
          rows={serviceOrder.attachments}
          columns={columns}
          rowHeight={25}
          columnHeaderHeight={30}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          pageSizeOptions={[10]}
          localeText={{ noRowsLabel: "No Hay Contactos de Obra" }}
          disableRowSelectionOnClick
        />

      </CssVarsProvider>
    </Box>
  );
};

export default AttachmentsList;
